/**
 * Admin authentication context.
 * Uses useReducer for predictable state management.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';

import type { AdminSession, AdminUser } from '@/api/types';
import { tokenStorage } from '@/api/tokenStorage';
import { wireRefreshSession } from '@/api/http';
import * as authApi from '@/api/authApi';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type AuthState =
  | { status: 'unknown' }
  | { status: 'unauthenticated' }
  | { status: 'authenticated'; admin: AdminUser; session: AdminSession }
  | { status: 'refreshing'; admin: AdminUser };

type AuthAction =
  | { type: 'INIT'; session: AdminSession | null }
  | { type: 'LOGIN_SUCCESS'; session: AdminSession }
  | { type: 'REFRESH_START'; admin: AdminUser }
  | { type: 'REFRESH_SUCCESS'; accessToken: string; expiresAt: string }
  | { type: 'REFRESH_ERROR' }
  | { type: 'LOGOUT' };

type AuthContextValue = {
  state: AuthState;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

function reducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'INIT':
      if (!action.session) return { status: 'unauthenticated' };
      return {
        status: 'authenticated',
        admin: action.session.admin,
        session: action.session,
      };
    case 'LOGIN_SUCCESS':
      return {
        status: 'authenticated',
        admin: action.session.admin,
        session: action.session,
      };
    case 'REFRESH_START':
      return { status: 'refreshing', admin: action.admin };
    case 'REFRESH_SUCCESS': {
      if (state.status !== 'refreshing' && state.status !== 'authenticated') {
        return { status: 'unauthenticated' };
      }
      const prevAdmin =
        state.status === 'refreshing'
          ? state.admin
          : state.status === 'authenticated'
            ? state.admin
            : null;
      if (!prevAdmin) return { status: 'unauthenticated' };
      const prevSession = state.status === 'authenticated' ? state.session : null;
      return {
        status: 'authenticated',
        admin: prevAdmin,
        session: {
          ...(prevSession ?? {
            admin: prevAdmin,
            refreshToken: '',
            expiresAt: '',
          }),
          accessToken: action.accessToken,
          expiresAt: action.expiresAt,
        },
      };
    }
    case 'REFRESH_ERROR':
      return { status: 'unauthenticated' };
    case 'LOGOUT':
      return { status: 'unauthenticated' };
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, { status: 'unknown' });

  // PHASE 8A — A8-P1-1: route-clear helper used by the refresh interceptor
  // when the refresh itself fails. The callback must not throw; it dispatches
  // the reducer and clears sessionStorage. The AuthContext's `useReducer`
  // exposes the same state to every component subscribed via `useAuth`,
  // so the entire app reacts to a session-expiry at once.
  const handleSessionInvalid = useCallback(() => {
    tokenStorage.clear();
    dispatch({ type: 'REFRESH_ERROR' });
  }, []);

  // PHASE 8A — A8-P1-1: wire the global refresh interceptor so it can read
  // the refresh token, write the new access token, and call
  // `handleSessionInvalid` if the refresh fails. We re-wire on every state
  // transition so the closure always sees the latest token.
  useEffect(() => {
    void state; // explicit dependency capture for ESLint; the closure below reads `state` directly.
    wireRefreshSession({
      getRefreshToken: () => tokenStorage.get()?.refreshToken ?? null,
      setSession: (s) => {
        const stored = tokenStorage.get();
        if (!stored) return;
        tokenStorage.set({
          ...stored,
          accessToken: s.accessToken,
          expiresAt: s.expiresAt,
        });
        // Dispatch REFRESH_SUCCESS so the in-memory session also reflects
        // the new token, plus the current admin user. Use whichever admin
        // context value we have right now.
        const admin =
          (state.status === 'authenticated' ? state.admin : null) ??
          (state.status === 'refreshing' ? state.admin : null);
        if (admin) {
          dispatch({
            type: 'REFRESH_SUCCESS',
            accessToken: s.accessToken,
            expiresAt: s.expiresAt,
          });
        }
      },
      onSessionInvalid: () => handleSessionInvalid(),
    });
    // Intentionally re-wire on every auth state change so the refresh
    // closure always uses the current in-memory session.
    return () => {
      // No-op cleanup; the next render will rewire.
    };
  }, [state, handleSessionInvalid]);

  // On mount: rehydrate session from storage.
  useEffect(() => {
    const stored = tokenStorage.get();
    if (stored) {
      authApi
        .getCurrentAdmin()
        .then(({ admin }) => {
          tokenStorage.set({
            accessToken: stored.accessToken,
            refreshToken: stored.refreshToken,
            expiresAt: stored.expiresAt,
            userId: stored.userId,
            username: admin.user?.username ?? stored.username,
            role: admin.role,
          });
          dispatch({
            type: 'INIT',
            session: {
              accessToken: stored.accessToken,
              refreshToken: stored.refreshToken,
              expiresAt: stored.expiresAt,
              admin,
            },
          });
        })
        .catch(() => {
          tokenStorage.clear();
          dispatch({ type: 'INIT', session: null });
        });
    } else {
      dispatch({ type: 'INIT', session: null });
    }
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const session = await authApi.loginAdmin(email, password);
    // Backend returns `accessTokenExpiresIn` (seconds) and `refreshTokenExpiresAt`
    // (ISO string). Map them into the canonical `expiresAt` field so the rest
    // of the app (and tokenStorage) can use a single expiry check.
    const accessExpiresAt = new Date(
      Date.now() + (session.accessTokenExpiresIn ?? 900) * 1000,
    ).toISOString();
    const sessionRecord: AdminSession = {
      accessToken: session.accessToken,
      refreshToken: session.refreshToken,
      expiresAt: session.refreshTokenExpiresAt ?? accessExpiresAt,
      admin: session.admin,
    };
    tokenStorage.set({
      accessToken: sessionRecord.accessToken,
      refreshToken: sessionRecord.refreshToken,
      expiresAt: sessionRecord.expiresAt,
      userId: sessionRecord.admin.user_id,
      username: sessionRecord.admin.user?.username ?? email,
      role: sessionRecord.admin.role,
    });
    dispatch({ type: 'LOGIN_SUCCESS', session: sessionRecord });
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logoutAdmin();
    } finally {
      tokenStorage.clear();
      dispatch({ type: 'LOGOUT' });
    }
  }, []);

  const value = useMemo<AuthContextValue>(() => ({ state, login, logout }), [state, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
